import React, { useMemo, useState, useCallback, useRef } from 'react';
import useStore from '../store';

function parseTime(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h + m / 60;
}

function formatHourLabel(hours) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h >= 24) return `${String(h - 24).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export default function StatsScreen() {
  const banya = useStore((s) => s.banya);

  // Пастельная палитра цветов для участников
  const colors = [
    { bg: '#ff8a80' },
    { bg: '#82b1ff' },
    { bg: '#b9f6ca' },
    { bg: '#ffd740' },
    { bg: '#e0b0ff' },
    { bg: '#80deea' },
    { bg: '#ffab91' },
    { bg: '#aed581' },
    { bg: '#f48fb1' },
    { bg: '#9fa8da' },
  ];

  const [hoverTime, setHoverTime] = useState(null);
  const [hoverPct, setHoverPct] = useState(null);
  const [selectedAward, setSelectedAward] = useState(null);
  const timelineRef = useRef(null);
  const closeAwardHint = useCallback(() => setSelectedAward(null), []);

  const handlePointerMove = useCallback((e) => {
    const el = timelineRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = (x / rect.width) * 100;
    setHoverPct(Math.max(0, Math.min(100, pct)));
  }, []);

  const handlePointerLeave = useCallback(() => {
    setHoverTime(null);
    setHoverPct(null);
  }, []);

  const ganttData = useMemo(() => {
    if (!banya) return null;

    const { startTime, endTime, people } = banya;
    const start = parseTime(startTime);
    let end = parseTime(endTime);
    if (end <= start) end += 24;

    const totalMinutes = (end - start) * 60;

    const rows = people.map((p) => {
      let a = parseTime(p.arrivedAt);
      let l = parseTime(p.leftAt);
      if (l <= a) l += 24;
      if (a < start) a = start;
      if (l > end) l = end;

      const leftPct = ((a - start) / (end - start)) * 100;
      const widthPct = ((l - a) / (end - start)) * 100;

      return {
        id: p.id,
        name: p.name,
        emoji: p.emoji,
        arrivedAt: p.arrivedAt,
        leftAt: p.leftAt,
        arrivedMinutes: Math.round((a - start) * 60),
        durationMinutes: Math.round((l - a) * 60),
        leftPct,
        widthPct,
      };
    });

    // Generate time labels every 30 minutes
    const totalSlots = Math.ceil(totalMinutes / 30);
    const labels = [];
    for (let i = 0; i <= totalSlots; i++) {
      const h = start + (i * 30) / 60;
      if (h <= end) {
        labels.push({
          time: h,
          label: formatHourLabel(h),
          leftPct: ((h - start) / (end - start)) * 100,
        });
      }
    }

    return { start, end, totalMinutes, rows, labels };
  }, [banya]);

  // Расчёт ачивок
  const awardsData = useMemo(() => {
    if (!banya || !ganttData || !ganttData.rows || ganttData.rows.length === 0) return {};

    const { startTime, endTime, hourlyRate, broomCost, people } = banya;
    const start = parseTime(startTime);
    let end = parseTime(endTime);
    if (end <= start) end += 24;
    const totalHours = end - start;
    const totalBanyaCost = hourlyRate * totalHours;
    const totalCost = totalBanyaCost + broomCost;
    const costPerHour = totalCost / totalHours;

    const personTimes = people.map((p) => {
      let a = parseTime(p.arrivedAt);
      let l = parseTime(p.leftAt);
      if (l <= a) l += 24;
      if (a < start) a = start;
      if (l > end) l = end;
      return { ...p, arrivedAtNum: a, leftAtNum: l };
    });

    const evts = [];
    evts.push({ time: start, type: 'start' });
    evts.push({ time: end, type: 'end' });
    personTimes.forEach((p) => {
      evts.push({ time: p.arrivedAtNum, type: 'arrive', personId: p.id });
      evts.push({ time: p.leftAtNum, type: 'leave', personId: p.id });
    });
    evts.sort((a, b) => a.time - b.time);

    const banks = [];
    let curPeople = [];
    let curTime = start;
    for (const ev of evts) {
      if (ev.time === curTime) {
        if (ev.type === 'arrive') { const pp = personTimes.find(x => x.id === ev.personId); if (pp) curPeople.push(pp); }
        else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
        continue;
      }
      if (ev.time > curTime) {
        const dur = ev.time - curTime;
        if (curPeople.length > 0) banks.push({ from: curTime, to: ev.time, dur, people: [...curPeople] });
        curTime = ev.time;
        if (ev.type === 'arrive') { const pp = personTimes.find(x => x.id === ev.personId); if (pp) curPeople.push(pp); }
        else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
      }
    }

    const personCosts = {};
    personTimes.forEach((p) => { personCosts[p.id] = { total: 0 }; });
    banks.forEach((bank) => {
      const bankCost = costPerHour * bank.dur;
      const share = bankCost / bank.people.length;
      bank.people.forEach((p) => { personCosts[p.id].total += share; });
    });

    const maxOverlapPerPerson = {};
    personTimes.forEach((p) => { maxOverlapPerPerson[p.id] = 0; });
    banks.forEach((bank) => {
      const count = bank.people.length;
      bank.people.forEach((p) => {
        if (count > maxOverlapPerPerson[p.id]) maxOverlapPerPerson[p.id] = count;
      });
    });

    const durations = personTimes.map(p => ({ id: p.id, dur: p.leftAtNum - p.arrivedAtNum }));
    const arrivalTimes = personTimes.map(p => ({ id: p.id, time: p.arrivedAtNum }));
    const departureTimes = personTimes.map(p => ({ id: p.id, time: p.leftAtNum }));

    const minArrival = Math.min(...arrivalTimes.map(x => x.time));
    const maxArrival = Math.max(...arrivalTimes.map(x => x.time));
    const minDeparture = Math.min(...departureTimes.map(x => x.time));
    const minDuration = Math.min(...durations.map(x => x.dur));
    const minCost = Math.min(...Object.values(personCosts).map(x => x.total));

    const awards = {};

    people.forEach((p) => {
      const pt = personTimes.find(x => x.id === p.id);
      const dur = pt.leftAtNum - pt.arrivedAtNum;
      const cost = personCosts[p.id].total;
      const maxOverlap = maxOverlapPerPerson[p.id];
      const list = [];

      if (pt.leftAtNum === minDeparture) list.push({ title: 'первый у финиша', emoji: '🏁' });
      if (Math.abs(pt.arrivedAtNum - start) < 0.01 && Math.abs(pt.leftAtNum - end) < 0.01) list.push({ title: 'сторож', emoji: '🕛' });
      if (pt.arrivedAtNum === maxArrival) list.push({ title: 'самый поздний', emoji: '🐢' });
      const midStart = start + (end - start) / 2;
      if (pt.arrivedAtNum > start + 1/60 && pt.leftAtNum < end - 1/60 && pt.arrivedAtNum <= midStart && pt.leftAtNum >= midStart) list.push({ title: 'посредник', emoji: '🥕' });
      if (Math.abs(pt.arrivedAtNum - start) < 1 / 60) list.push({ title: 'пунктуальный', emoji: '🫡' });
      if (Math.abs(cost - minCost) < 0.01) list.push({ title: 'экономный', emoji: '💰' });
      if (Math.abs(dur - minDuration) < 0.01) list.push({ title: 'занятой', emoji: '🐗' });
      const maxOverlapOverall = Math.max(...Object.values(maxOverlapPerPerson));
      if (maxOverlap === maxOverlapOverall) list.push({ title: 'вайбовый', emoji: '🤩' });

      awards[p.id] = list;
    });

    return awards;
  }, [banya, ganttData]);

  if (!banya || !ganttData) {
    return (
      <div className="calc-section">
        <div className="calc-card"><p>Нет данных</p></div>
      </div>
    );
  }

  const { rows, labels } = ganttData;

  return (
    <div className="calc-section">
      <div className="stats-gantt-wrapper">
        {/* Левая колонка с именами (фиксированная) */}
        <div className="stats-gantt-sidebar">
          {rows.map((row, idx) => {
            const c = colors[idx % colors.length];
            return (
              <div className="stats-gantt-name-row" key={row.id}>
                <span className="stats-name-label"
                  style={{ backgroundColor: c.bg }}
                ><span className="stats-name-label-inner"><span className="emoji-inline">{row.emoji || '😎'}</span><span>{row.name}</span></span></span>
              </div>
            );
          })}
        </div>

        {/* Область с таймлайном */}
        <div className="stats-gantt-timeline-wrapper"
          ref={timelineRef}
          onMouseMove={handlePointerMove}
          onMouseLeave={handlePointerLeave}
          onTouchMove={(e) => { e.preventDefault(); const t = e.touches[0]; handlePointerMove({ clientX: t.clientX, clientY: t.clientY }); }}
          onTouchStart={(e) => { const t = e.touches[0]; handlePointerMove({ clientX: t.clientX, clientY: t.clientY }); }}
          onTouchEnd={handlePointerLeave}
          style={{ position: 'relative', cursor: 'crosshair' }}
        >
          {hoverPct !== null && (() => {
            const { start } = ganttData;
            const end = ganttData.end;
            const timeVal = start + (end - start) * (hoverPct / 100);
            const h = Math.floor(timeVal);
            const m = Math.round((timeVal - h) * 60);
            const label = (h >= 24 ? String(h - 24).padStart(2, '0') : String(h).padStart(2, '0')) + ':' + String(m).padStart(2, '0');
            return (
              <>
                <div className="gantt-crosshair-line" style={{ left: `${hoverPct}%` }} />
              </>
            );
          })()}

          {/* Строки с барами */}
          <div className="stats-gantt-rows">
            {rows.map((row, idx) => {
              const c = colors[idx % colors.length];
              return (
                <div className="stats-gantt-row" key={row.id}>
                  <div
                    className="stats-gantt-bar"
                    style={{
                      left: `${row.leftPct}%`,
                      width: `${row.widthPct}%`,
                      backgroundColor: c.bg,
                    }}
                    title={`${row.name}: ${row.arrivedAt}—${row.leftAt} (${Math.floor(row.durationMinutes / 60)}ч ${row.durationMinutes % 60}мин)`}
                  >
                    {row.widthPct > 15 && (
                      <span className="stats-gantt-bar-text">
                        {row.arrivedAt}—{row.leftAt}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {hoverPct !== null && (() => {
            const { start, end } = ganttData;
            const timeVal = start + (end - start) * (hoverPct / 100);
            const h = Math.floor(timeVal);
            const m = Math.round((timeVal - h) * 60);
            const timeLabel = (h >= 24 ? String(h - 24).padStart(2, '0') : String(h).padStart(2, '0')) + ':' + String(m).padStart(2, '0');
            // Find people who are in the bath at this moment
            const timeHours = h + m / 60;
            const activeEmojis = rows
              .filter(row => {
                const a = parseTime(row.arrivedAt);
                let l = parseTime(row.leftAt);
                if (l <= a) l += 24;
                return timeHours >= a && timeHours <= l;
              })
              .map(row => row.emoji || '😎');
            const emojiStr = activeEmojis.length > 0 ? activeEmojis.join('') : '—';
            return (
              <div className="gantt-crosshair-hint" style={{ left: `${hoverPct}%` }}>
                <div className="hint-time"><span className="hint-label">Время:</span> {timeLabel}{(() => {
                  // Rebuild banks from banya events (same logic as CalcScreen)
                  const s = parseTime(banya.startTime);
                  let e = parseTime(banya.endTime);
                  if (e <= s) e += 24;
                  const pt = banya.people.map(p => {
                    let a = parseTime(p.arrivedAt);
                    let l = parseTime(p.leftAt);
                    if (l <= a) l += 24;
                    if (a < s) a = s;
                    if (l > e) l = e;
                    return { ...p, arrivedAtNum: a, leftAtNum: l };
                  });
                  const evts = [];
                  evts.push({ time: s, type: 'start' });
                  evts.push({ time: e, type: 'end' });
                  pt.forEach(p => { evts.push({ time: p.arrivedAtNum, type: 'arrive', personId: p.id }); evts.push({ time: p.leftAtNum, type: 'leave', personId: p.id }); });
                  evts.sort((a, b) => a.time - b.time);
                  const banks = [];
                  let curPeople = [];
                  let curTime = s;
                  for (const ev of evts) {
                    if (ev.time === curTime) {
                      if (ev.type === 'arrive') { const p = pt.find(x => x.id === ev.personId); if (p) curPeople.push(p); }
                      else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
                      continue;
                    }
                    if (ev.time > curTime) {
                      const dur = ev.time - curTime;
                      if (curPeople.length > 0) banks.push({ from: curTime, to: ev.time, dur, people: [...curPeople] });
                      curTime = ev.time;
                      if (ev.type === 'arrive') { const p = pt.find(x => x.id === ev.personId); if (p) curPeople.push(p); }
                      else if (ev.type === 'leave') { curPeople = curPeople.filter(x => x.id !== ev.personId); }
                    }
                  }
                  const bank = banks.find(b => timeHours >= b.from && timeHours < b.to);
                  return bank ? <span className="hint-bank-sep"> · </span> : null;
                })()}{(() => {
                  // Rebuild banks again (sorry, can't reuse IIFE vars) - find bank
                  const s2 = parseTime(banya.startTime);
                  let e2 = parseTime(banya.endTime);
                  if (e2 <= s2) e2 += 24;
                  const pt2 = banya.people.map(p => {
                    let a = parseTime(p.arrivedAt);
                    let l = parseTime(p.leftAt);
                    if (l <= a) l += 24;
                    if (a < s2) a = s2;
                    if (l > e2) l = e2;
                    return { ...p, arrivedAtNum: a, leftAtNum: l };
                  });
                  const evts2 = [];
                  evts2.push({ time: s2, type: 'start' });
                  evts2.push({ time: e2, type: 'end' });
                  pt2.forEach(p => { evts2.push({ time: p.arrivedAtNum, type: 'arrive', personId: p.id }); evts2.push({ time: p.leftAtNum, type: 'leave', personId: p.id }); });
                  evts2.sort((a, b) => a.time - b.time);
                  const banks2 = [];
                  let curPeople2 = [];
                  let curTime2 = s2;
                  for (const ev of evts2) {
                    if (ev.time === curTime2) {
                      if (ev.type === 'arrive') { const p = pt2.find(x => x.id === ev.personId); if (p) curPeople2.push(p); }
                      else if (ev.type === 'leave') { curPeople2 = curPeople2.filter(x => x.id !== ev.personId); }
                      continue;
                    }
                    if (ev.time > curTime2) {
                      const dur = ev.time - curTime2;
                      if (curPeople2.length > 0) banks2.push({ from: curTime2, to: ev.time, dur, people: [...curPeople2] });
                      curTime2 = ev.time;
                      if (ev.type === 'arrive') { const p = pt2.find(x => x.id === ev.personId); if (p) curPeople2.push(p); }
                      else if (ev.type === 'leave') { curPeople2 = curPeople2.filter(x => x.id !== ev.personId); }
                    }
                  }
                  const bank = banks2.find(b => timeHours >= b.from && timeHours < b.to);
                  return bank ? <span className="hint-bank-name">Банк {banks2.indexOf(bank) + 1}</span> : null;
                })()}</div>
                <div className="hint-vibe-row"><span className="hint-label">Вайб:</span> <span className="hint-emojis">{emojiStr}</span></div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Люди */}
      {banya && banya.people && banya.people.length > 0 && (
        <div className="stats-people-grid">
          {banya.people.map((person, idx) => {
            const c = colors[idx % colors.length];
            return (
              <div className="stats-people-item" key={person.id}>
                <div className="stats-people-emoji">
                  {person.emoji || '😎'}
                </div>
                <div className="stats-people-name" style={{ backgroundColor: c.bg }}>
                  {person.name}
                </div>
                <div className="stats-people-card">
                  {(() => {
                    const personAwards = awardsData[person.id] || [];
                    if (personAwards.length === 0) return <span className="stats-no-awards">—</span>;
                    return personAwards.map((a, i) => (
                      <div className="stats-award-row" key={i} onClick={() => setSelectedAward({ ...a, personName: person.name })}>
                        <span className="stats-award-emoji">{a.emoji}</span>
                        <span className="stats-award-title">{a.title}</span>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedAward && (
        <div className="stats-award-overlay" onClick={closeAwardHint}>
          <div className="stats-award-hint">
            {(function() {
              const descriptions = {
                'первый у финиша': selectedAward.personName + ' покинул баню раньше всех',
                'сторож': selectedAward.personName + ' был в бане всё время от начала до конца',
                'самый поздний': selectedAward.personName + ' пришёл в баню позже всех',
                'посредник': selectedAward.personName + ' находился в бане в середине сессии',
                'пунктуальный': selectedAward.personName + ' пришёл ровно к началу',
                'экономный': selectedAward.personName + ' потратил меньше всех денег',
                'занятой': selectedAward.personName + ' провёл в бане меньше всех времени',
                'вайбовый': selectedAward.personName + ' застал наибольшее количество присутствующих одновременно',
              };
              return descriptions[selectedAward.title] || '';
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
